import { useState, useEffect } from 'react';
import { X, Search, Building2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { DiscountCategory, UserType } from '../../types/database';

interface Discount {
  id: string;
  title: string;
  description: string;
  discount_amount: string;
  category: string;
  vendor_id: string;
  is_active: boolean;
  expiry_date: string | null;
  valid_for: string[];
  image_url: string | null;
  terms: string | null;
}

interface Vendor {
  id: string;
  business_name: string;
  owner_name: string;
  logo_url: string | null;
  business_category: string;
  status: string;
}

interface DiscountModalProps {
  discount: Discount | null;
  onClose: () => void;
  onSave: () => void;
}

const CATEGORIES: DiscountCategory[] = ['food', 'retail', 'services', 'entertainment', 'education', 'health', 'other'];
const USER_TYPES: UserType[] = ['student', 'professional', 'resident', 'visitor'];

export function DiscountModal({ discount, onClose, onSave }: DiscountModalProps) {
  const [loading, setLoading] = useState(false);
  const [vendorId, setVendorId] = useState('');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    discount_amount: '',
    category: 'food' as DiscountCategory,
    valid_for: ['student', 'professional', 'resident', 'visitor'] as UserType[],
    image_url: '',
    terms: '',
    expiry_date: '',
  });

  useEffect(() => {
    loadApprovedVendors();
  }, []);

  useEffect(() => {
    if (discount) {
      setFormData({
        title: discount.title,
        description: discount.description,
        discount_amount: discount.discount_amount,
        category: discount.category as DiscountCategory,
        valid_for: (discount.valid_for || []) as UserType[],
        image_url: discount.image_url || '',
        terms: discount.terms || '',
        expiry_date: discount.expiry_date ? discount.expiry_date.split('T')[0] : '',
      });
      setVendorId(discount.vendor_id);
      loadVendorDetails(discount.vendor_id);
    }
  }, [discount]);

  const loadApprovedVendors = async () => {
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('id, business_name, owner_name, logo_url, business_category, status')
        .eq('status', 'approved')
        .order('business_name');

      if (error) throw error;
      setVendors(data || []);
    } catch (err) {
      console.error('Error loading vendors:', err);
    }
  };

  const loadVendorDetails = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('id, business_name, owner_name, logo_url, business_category, status')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (data) {
        setSelectedVendor(data);
      }
    } catch (err) {
      console.error('Error loading vendor details:', err);
    }
  };

  const filteredVendors = vendors.filter((vendor) =>
    vendor.business_name.toLowerCase().includes(vendorSearch.toLowerCase()) ||
    vendor.owner_name.toLowerCase().includes(vendorSearch.toLowerCase()) ||
    vendor.business_category.toLowerCase().includes(vendorSearch.toLowerCase())
  );

  const selectVendor = (vendor: Vendor) => {
    setSelectedVendor(vendor);
    setVendorId(vendor.id);
    setVendorSearch('');
    setShowVendorDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (discount) {
        const { error } = await supabase
          .from('discounts')
          .update({
            ...formData,
            expiry_date: formData.expiry_date || null,
          })
          .eq('id', discount.id);

        if (error) throw error;
      } else {
        if (!vendorId) {
          alert('Please select a vendor');
          setLoading(false);
          return;
        }

        const { error } = await supabase.from('discounts').insert({
          vendor_id: vendorId,
          ...formData,
          expiry_date: formData.expiry_date || null,
          status: 'approved',
          is_active: true,
        });

        if (error) throw error;
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving discount:', error);
      alert('Failed to save discount');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserType = (userType: UserType) => {
    setFormData(prev => ({
      ...prev,
      valid_for: prev.valid_for.includes(userType)
        ? prev.valid_for.filter(t => t !== userType)
        : [...prev.valid_for, userType]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            {discount ? 'Edit Discount' : 'Add New Discount'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Discount Title *
            </label>
            <input
              id="title"
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., 20% off all menu items"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="discount_amount" className="block text-sm font-medium text-gray-700 mb-2">
                Discount Amount *
              </label>
              <input
                id="discount_amount"
                type="text"
                required
                value={formData.discount_amount}
                onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., 20% off, $10 off"
              />
            </div>

            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as DiscountCategory })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              required
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Describe your discount offer..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Valid For *
            </label>
            <div className="flex flex-wrap gap-2">
              {USER_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleUserType(type)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    formData.valid_for.includes(type)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="terms" className="block text-sm font-medium text-gray-700 mb-2">
              Terms & Conditions
            </label>
            <textarea
              id="terms"
              rows={3}
              value={formData.terms}
              onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Any restrictions or conditions..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="image_url" className="block text-sm font-medium text-gray-700 mb-2">
                Image URL
              </label>
              <input
                id="image_url"
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://example.com/image.jpg"
              />
            </div>

            <div>
              <label htmlFor="expiry_date" className="block text-sm font-medium text-gray-700 mb-2">
                Expiry Date
              </label>
              <input
                id="expiry_date"
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {!discount && (
            <div>
              <label htmlFor="vendor" className="block text-sm font-medium text-gray-700 mb-2">
                Select Vendor *
              </label>
              <div className="relative">
                {selectedVendor ? (
                  <div className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg bg-gray-50">
                    {selectedVendor.logo_url ? (
                      <img
                        src={selectedVendor.logo_url}
                        alt={selectedVendor.business_name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
                        <Building2 size={20} className="text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{selectedVendor.business_name}</p>
                      <p className="text-sm text-gray-600 capitalize">{selectedVendor.business_category}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedVendor(null);
                        setVendorId('');
                      }}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        value={vendorSearch}
                        onChange={(e) => {
                          setVendorSearch(e.target.value);
                          setShowVendorDropdown(true);
                        }}
                        onFocus={() => setShowVendorDropdown(true)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Search for a vendor..."
                      />
                    </div>
                    {showVendorDropdown && (
                      <div className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredVendors.length === 0 ? (
                          <div className="p-4 text-center text-gray-500">
                            No approved vendors found
                          </div>
                        ) : (
                          filteredVendors.map((vendor) => (
                            <button
                              key={vendor.id}
                              type="button"
                              onClick={() => selectVendor(vendor)}
                              className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
                            >
                              {vendor.logo_url ? (
                                <img
                                  src={vendor.logo_url}
                                  alt={vendor.business_name}
                                  className="w-10 h-10 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
                                  <Building2 size={20} className="text-gray-400" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-gray-900">{vendor.business_name}</p>
                                <p className="text-sm text-gray-600">
                                  {vendor.owner_name} • {vendor.business_category}
                                </p>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Select from approved vendors who have completed their business profile
              </p>
            </div>
          )}

          <div className="flex space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : discount ? 'Update Discount' : 'Create Discount'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
